/* @flow */
import * as React from 'react';
import { useId } from 'react-aria';
import classNames from 'classnames';

// Styles
import styles from './OptionsMenu.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import messages from 'src/shared/ui/component/menu/l10n/menuL10n';

// Components
import MenuPopover from 'src/shared/ui/component/menu/MenuPopover';
import { MoreHoriz } from '@mui/icons-material';
import MenuPopoverItem from 'src/shared/ui/component/menu/MenuPopoverItem';

// Type
import type { MenuPopoverItemProps } from 'src/shared/ui/component/menu/MenuPopoverItem';
import { useIntl } from 'react-intl';
import { TOUR_SELECTOR_KEYS } from 'src/shared/app/coachmark/config/tourConfig';

const useStyles = createUseThemeStyles(styles);

type Props = {
  options: MenuPopoverItemProps[],
  classes?: {
    root?: string,
    button?: string,
  },
};

const OptionsMenu = ({ options, classes: customClasses = {} }: Props) => {
  const classes = useStyles();
  const intl = useIntl();

  const anchorId = useId();
  const anchorRef = React.useRef<HTMLElement | null>(null);

  const [isOpened, setIsOpened] = React.useState<boolean>(false);

  if (!options.length) return null;

  return (
    <div className={customClasses.root}>
      <button
        onClick={() => setIsOpened(true)}
        id={anchorId}
        ref={anchorRef}
        aria-label={intl.formatMessage(messages.moreOptions)}
        className={classNames(
          classes.button,
          customClasses.button,
          TOUR_SELECTOR_KEYS.OPTION_DROPDOWN_MENU,
        )}
        type="button"
      >
        <MoreHoriz />
      </button>

      <MenuPopover
        open={isOpened}
        anchorEl={anchorRef.current}
        ariaLabelledby={anchorId}
        onClose={() => setIsOpened(false)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        renderDrawerOnMobile
      >
        {options.map((item, key) => (
          <MenuPopoverItem
            {...item}
            onClick={() => {
              setIsOpened(false);
              if (item.onClick) item.onClick();
            }}
            key={`menu-item-${key}`}
          />
        ))}
      </MenuPopover>
    </div>
  );
};

export default React.memo<Props>(OptionsMenu);
